import { useState } from "react";
import { StyleSheet, View } from "react-native";
import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdInput } from "src/components/forms/input";
import { PdStack } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalDescription, PdModalTitle } from "src/components/overlay";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { useAppDispatch } from "src/redux";
import { adminUpdateUserEmail } from "src/redux/AdminUser/actions";
import { tModalComponentProps } from "src/redux/modals/types";
import { changeEmailRetailer } from "src/redux/user/actions";
import { tUser, tUserAccountType } from "tmt_api/src/types/types.validator";
import { useIsMobile } from "../ui";

type tChangeModalParams = {
  accountType: tUserAccountType;
  user?: tUser;
  companyId?: string;
};

const ChangeEmailModal: React.FC<tModalComponentProps<tChangeModalParams, string>> = ({
  onClose,
  onResolve,
  accountType,
  user,
  companyId,
}) => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const [sEmail, setEmail] = useState("");
  const [sConfirmEmail, setConfirmEmail] = useState("");
  const [isSubmitting, setSubmitting] = useState(false);

  const handleOnClose = () => {
    onClose();
  };

  const sendEmail = async () => {
    const email = sEmail.trim();
    const confirmEmail = sConfirmEmail.trim();
    if (confirmEmail !== email) return;

    try {
      setSubmitting(true);
      if (accountType === "retailer" && companyId) {
        await dispatch(changeEmailRetailer(companyId, email));
      } else if (user) {
        await dispatch(adminUpdateUserEmail(email, user));
      }
      onResolve(email);
    } catch (error) {
      setSubmitting(false);
    }
  };

  return (
    <PdModal onClose={handleOnClose} closeOnOverlayClick={!isSubmitting}>
      <PdModalCloseButton onPress={handleOnClose} isDisabled={isSubmitting} />
      <View style={styles.modal}>
        <PdModalTitle style={styles.confirmModalTitle}>Change Email</PdModalTitle>
        <PdModalDescription>This will change a users login email. Only do this if you are sure.</PdModalDescription>
        <PdStack layout={[1, 1]} direction={isMobile ? "vertical" : "horizontal"} style={styles.mb10}>
          <View>
            <PdFormLabel>Email</PdFormLabel>
            <PdInput value={sEmail} onChangeText={setEmail} />
          </View>
          <View>
            <PdFormLabel>Confirm Email</PdFormLabel>
            <PdInput value={sConfirmEmail} onChangeText={setConfirmEmail} />
          </View>
        </PdStack>
        <PdStack isReversed={isMobile} direction={isMobile ? "vertical" : "horizontal"} style={styles.justifyBetween}>
          <PdButton
            colorScheme="purple"
            variant="outline"
            isDisabled={isSubmitting}
            onPress={handleOnClose}
            style={styles.minWMd}
          >
            <PdButtonLabel>Cancel</PdButtonLabel>
          </PdButton>
          <PdButton onPress={sendEmail} isLoading={isSubmitting} style={styles.minWMd}>
            <PdButtonLabel>Submit</PdButtonLabel>
          </PdButton>
        </PdStack>
      </View>
    </PdModal>
  );
};

const useChangeEmailModal = (): tUseModal<tChangeModalParams, string> => {
  return useModal<tChangeModalParams, string>(ChangeEmailModal);
};

export default useChangeEmailModal;

const styles = StyleSheet.create({
  confirmModalTitle: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb10: {
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  minWMd: {
    minWidth: 150,
  },
  modal: {
    backgroundColor: kColors.white,
    maxWidth: 500,
    padding: 6 * kStyleConst.kSpacing,
  },
});
